import { Button, Checkbox, Divider, InlineStack, Page, Spinner, Text, TextField, BlockStack } from '@shopify/polaris'
import React, { useEffect, useState } from 'react'
import { CedAmazonIcon, CircleTickIcon } from '../../../../../Asests/svgIcon/icon'
import queryString from 'query-string';
import { DI, DIProps } from '../../../../../Core';

const UnsubscribePage = (props: DIProps) => {
    const queryParams: any = queryString.parse(props.location.search);

    const [subscribed, setSubscribed] = useState(0);
    const [resubscribe_token, setResubscribeToken] = useState('');
    const [loader, setLoader] = useState(false)

    const checkSubscription = () => {
        setLoader(true)
        props.di.POST('connector/email/checkSubscription', {
            token: queryParams?.token ?? ''
        }).then(res => {
            setLoader(false)
            if (res.success) {
                setSubscribed(+!!res?.already_implemented)
                res.new_token && setResubscribeToken(res.new_token)
            } else {
                props.error(res?.message ?? 'Something went wrong')
            }
        })
    }

    useEffect(() => {
        checkSubscription();
    }, [])

    return (
        <Page>
            <div style={{ paddingTop: '128px', maxWidth: '1062px', width: '100%', margin: '0 auto' }}>
                <BlockStack gap="3200" align='center'>
                    <div style={{ maxWidth: '576px', width: '100%', margin: '0 auto' }}>
                        <BlockStack gap="300" align='center'>
                            <InlineStack gap="300" blockAlign='center' align='center'>
                                <CedAmazonIcon />
                                <Text as="span">CedCommerce Amazon Channel</Text>
                            </InlineStack>
                            <Divider />
                        </BlockStack>
                    </div>
                    {loader && <InlineStack align='center'><Spinner /></InlineStack>}
                    {!loader && subscribed === 0 && <WantToUnsubscribe {...props} subscribed={subscribed} setSubscribed={setSubscribed} resubscribe_token={resubscribe_token} setResubscribeToken={setResubscribeToken} />}
                    {!loader && subscribed === 1 && <Unsubscribed {...props} subscribed={subscribed} setSubscribed={setSubscribed} resubscribe_token={resubscribe_token} setResubscribeToken={setResubscribeToken} />}
                </BlockStack>
            </div>
        </Page >
    )
}

export default DI(UnsubscribePage);

interface PropsI {
    subscribed: number;
    setSubscribed: React.Dispatch<React.SetStateAction<number>>
    resubscribe_token: string;
    setResubscribeToken: React.Dispatch<React.SetStateAction<string>>

}

const WantToUnsubscribe = (props: DIProps & PropsI) => {
    const queryParams: any = queryString.parse(props.location.search);

    const [reasonData, setReasonData] = useState<{ [key: string]: { checked: boolean; value?: string, error?: boolean } }>({
        frequent_email: { checked: false, value: 'You email too frequently' },
        unrelevant: { checked: false, value: 'This content is no longer relevant to me' },
        never_signed: { checked: false, value: 'I never signed up for your emails' },
        spam: { checked: false, value: 'The emails are spam and should be reported' },
        other: { checked: false, value: '', error: false }
    });
    const [loader, setLoader] = useState(false);

    const onChange = (checked: boolean, key: string) => {
        setReasonData(a => ({ ...a, [key]: { ...a[key], checked } }))
    }

    const onTextChange = (value: string) => {
        setReasonData(a => ({ ...a, other: { ...a.other, value } }))
    }

    const onUnsubscribe = () => {
        if (reasonData.other.checked && !reasonData.other.value) {
            setReasonData(a => ({ ...a, other: { ...a.other, error: true } }))
            props.error('Please specify the reason');
            return;
        }

        setLoader(true);
        props.di.POST('connector/email/processEmailSubscription', {
            token: queryParams?.token ?? '',
            reasons: Object.values(reasonData).map((reason) => {
                return reason.checked && reason.value && reason.value
            }).filter(a => a)
        }).then(res => {
            setLoader(false)
            if (res.success) {
                props.setSubscribed(res?.already_implemented ? +!!res?.already_implemented : 1);
                res.new_token && props.setResubscribeToken(res.new_token)
            } else {
                props.error(res?.message ?? 'Failed to unsubscribe')
            }
        })
    }

    return <BlockStack gap="500" inlineAlign='center'>
        <BlockStack gap="400" align='center' inlineAlign='center'>
            <Text variant="heading3xl" as="h1">
                Want to Unsubscribe?
            </Text>
            <div style={{ textAlign: 'center' }}>
                <Text variant="headingLg" as="p" fontWeight="regular" tone='subdued'>
                    We will no longer send you any emails{queryParams?.mail_type ? ` related to the ${queryParams.mail_type}` : ''}, unless you request that we contact you.
                    Select a reason for unsubscribing to help us improve your experience
                </Text>
            </div>
        </BlockStack>

        <BlockStack gap="100">
            <Checkbox
                label="You email too frequently"
                checked={reasonData.frequent_email.checked}
                onChange={(checked) => { onChange(checked, 'frequent_email') }}
            />
            <Checkbox
                label="This content is no longer relevant to me"
                checked={reasonData.unrelevant.checked}
                onChange={(checked) => { onChange(checked, 'unrelevant') }}
            />
            <Checkbox
                label="I never signed up for your emails"
                checked={reasonData.never_signed.checked}
                onChange={(checked) => { onChange(checked, 'never_signed') }}
            />
            <Checkbox
                label="The emails are spam and should be reported"
                checked={reasonData.spam.checked}
                onChange={(checked) => { onChange(checked, 'spam') }}
            />
            <InlineStack gap="500">
                <Checkbox
                    label="Others"
                    checked={reasonData.other.checked}
                    onChange={(checked) => { onChange(checked, 'other') }}
                />
                <TextField
                    label=""
                    autoComplete="off"
                    value={reasonData.other?.value ?? ''}
                    onChange={onTextChange}
                    disabled={loader || !reasonData.other.checked}
                    error={reasonData.other.error}
                />
            </InlineStack>
        </BlockStack>
        <Button fullWidth variant='primary' onClick={() => {
            onUnsubscribe()
        }} loading={loader}>Unsubscribe Now</Button>
    </BlockStack>
}

const Unsubscribed = (props: DIProps & PropsI) => {
    const [loader, setLoader] = useState(false);
    const [showResubs, setShowResubs] = useState(false)

    const resubscribre = () => {
        setLoader(true);

        props.di.POST('connector/email/processEmailSubscription', {
            token: props.resubscribe_token ?? ''
        }).then(res => {
            setLoader(false)
            if (res.success) {
                setShowResubs(true)
            } else {
                props.error(res?.message ?? 'Failed to resubscribe')
            }
        })
    }
    return <BlockStack gap="800" align='center' inlineAlign='center'>
        <BlockStack gap="400" align='center' inlineAlign='center'>
            <InlineStack gap="400" blockAlign='center' wrap={false}>
                <CircleTickIcon />
                <Text variant="heading3xl" as="h1">
                    You’re {showResubs ? 'resubscribed' : 'unsubscribed'}.
                </Text>
            </InlineStack>
            {!showResubs && <Text variant="headingLg" as="p" fontWeight="regular" tone='subdued'>
                We will no longer send you any emails, unless you request that we contact you.
            </Text>}
        </BlockStack>
        {!showResubs && <Button variant="monochromePlain" onClick={() => { resubscribre() }} loading={loader}>
            Resubscribe
        </Button>}
    </BlockStack>
}